import React, { useState } from 'react';
import { ScreenProps } from '../App';
import { ClientData } from '../types/ClientData';

const InputField: React.FC<{label: string; value: string | number; onChange: (v: string) => void; type?: string; placeholder?: string}> = ({label, value, onChange, type = 'text', placeholder}) => (
  <div className="mb-4">
    <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white" />
  </div>
);

const ClientSetup: React.FC<ScreenProps> = ({ data, setData }) => {
  const update = (key: keyof typeof data) => (value: string) => setData(prev => ({ ...prev, [key]: key === 'clientAge' || key === 'partnerAge' ? Number(value) : value }));

  const isReady = data.clientName && data.businessName && data.adviserName;

  // ── Tally integration state ────────────────────────────────────────────────
  const [tallyEmail, setTallyEmail] = useState('');
  const [tallyLoading, setTallyLoading] = useState(false);
  const [tallyStatus, setTallyStatus] = useState<'idle' | 'success' | 'error' | 'notfound' | 'partial'>('idle');
  const [tallyFieldCount, setTallyFieldCount] = useState(0);

  // ── Tally fetch handler ────────────────────────────────────────────────────
  const handleLoadFromTally = async () => {
    if (!tallyEmail.trim()) return;
    setTallyLoading(true);
    setTallyStatus('idle');

    try {
      const url = `https://eo8gjbbxknt28ey.m.pipedream.net?email=${encodeURIComponent(tallyEmail.trim().toLowerCase())}`;
      const res = await fetch(url);

      if (res.status === 404) {
        setTallyStatus('notfound');
        setTallyLoading(false);
        return;
      }

      if (!res.ok) {
        setTallyStatus('error');
        setTallyLoading(false);
        return;
      }

      const responseData = await res.json();

      // Map Pipedream response keys → ClientData field names
      // Some Pipedream keys differ from ClientData — mapped here
      const fieldMap: Record<string, keyof ClientData> = {
        clientEmail: 'clientEmail',
        clientName: 'clientName',
        businessName: 'businessName',
        clientAge: 'clientAge',
        partnerAge: 'partnerAge',
        desiredRetirementAge: 'targetFreedomAge',
        salary: 'salary',
        dividends: 'dividends',
        otherIncome: 'otherIncome',
        expensesReimbursement: 'expensesReimbursement',
        monthlyHMRC: 'monthlyHMRC',
        pensionBalanceDC: 'pensionBalanceDC',
        employerPensionMonthly: 'pensionCompanyFunded',
        hasDBPension: 'hasDBPension',
        dbPensionScheme: 'dbPensionScheme',
        dbPensionAccruedAnnual: 'dbPensionAccruedAnnual',
        dbPensionNRA: 'dbPensionNRA',
        isaCash: 'isaCash',
        isaStocksShares: 'isaStocksShares',
        isaContribution: 'isaContribution',
        efBalance: 'efAccount1Balance',
        propertyValue: 'propertyValue',
        mortgageBalance: 'mortgageBalance',
        mortgagePayment: 'mortgagePayment',
        mortgageRate: 'mortgageRate',
        mortgageOP: 'mortgageOP',
        totalUnsecuredDebt: 'consumerDebtTotal',
        rentalPropertyCount: 'rentalPropertyCount',
        rentalEquityTotal: 'rentalEquityTotal',
        rentalMonthlyGrossIncome: 'rentalMonthlyGrossIncome',
        rentalMonthlyMortgageCosts: 'rentalMonthlyMortgageCosts',
        rentalMonthlyRunningCosts: 'rentalMonthlyRunningCosts',
        childMonthlyCommittedCosts: 'childMonthlyCommittedCosts',
        childCostsEndAge: 'childCostsEndAge',
        eisSeisBalance: 'eisSeisBalance',
        isCouple: 'isCouple',
        partnerName: 'partnerName',
        partnerSalary: 'partnerSalary',
        partnerDividends: 'partnerDividends',
        partnerOtherIncome: 'partnerOtherIncome',
        partnerPensionBalanceDC: 'partnerPensionBalanceDC',
        partnerEmployerPensionMonthly: 'partnerEmployerPensionMonthly',
        partnerHasDBPension: 'partnerHasDBPension',
        partnerDBPensionScheme: 'partnerDBPensionScheme',
        partnerDBPensionAccruedAnnual: 'partnerDBPensionAccruedAnnual',
        partnerDBPensionNRA: 'partnerDBPensionNRA',
        partnerISACash: 'partnerISACash',
        partnerISAStocksShares: 'partnerISAStocksShares',
        partnerISAMonthly: 'partnerISAMonthly',
        businessOperatingBufferBalance: 'businessOperatingBufferBalance',
        businessTaxReserveBalance: 'businessTaxReserveBalance',
        businessEstimatedCTLiability: 'businessEstimatedCTLiability',
        businessDLABalance: 'businessDLABalance',
        intakeNotes: 'intakeNotes',
        intakeSubmittedAt: 'intakeSubmittedAt',
      };

      const updates: Partial<ClientData> = {};
      let count = 0;
      for (const [responseKey, clientDataKey] of Object.entries(fieldMap)) {
        if (responseData[responseKey] !== undefined && responseData[responseKey] !== null) {
          (updates as Record<string, unknown>)[clientDataKey] = responseData[responseKey];
          count++;
        }
      }

      // Sync backward-compat fields
      if (updates.isaCash !== undefined || updates.isaStocksShares !== undefined) {
        updates.isaBalance = ((updates.isaCash as number) ?? 0) + ((updates.isaStocksShares as number) ?? 0);
        updates.investmentsBalance = (updates.isaStocksShares as number) ?? 0;
      }
      if (updates.pensionBalanceDC !== undefined) {
        updates.pensionBalance = updates.pensionBalanceDC as number;
      }

      setData(prev => ({ ...prev, ...updates }));
      setTallyFieldCount(count);
      // Core fields that should always be present in a complete submission
      const coreFields: (keyof ClientData)[] = ['clientName', 'businessName', 'clientAge', 'salary'];
      const missingCore = coreFields.some(f => updates[f] === undefined);
      setTallyStatus(count > 0 && missingCore ? 'partial' : 'success');

    } catch {
      setTallyStatus('error');
    } finally {
      setTallyLoading(false);
    }
  };

  return (
    <div className="flex h-full">
      {/* LEFT: Inputs */}
      <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 p-6 overflow-y-auto">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Client Details</div>
        <InputField label="Client Name" value={data.clientName} onChange={update('clientName')} placeholder="Client name" />
        <InputField label="Business Name" value={data.businessName} onChange={update('businessName')} placeholder="Business name" />
        <InputField label="Session Date" value={data.sessionDate} onChange={update('sessionDate')} placeholder="e.g. 15/05/2026" />
        <InputField label="Adviser Name" value={data.adviserName} onChange={update('adviserName')} placeholder="Adviser name" />
        <div className="border-t border-gray-100 my-4"></div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Client Ages</div>
        <InputField label="Client Age" value={data.clientAge || ''} onChange={update('clientAge')} type="number" placeholder="e.g. 60" />
        <InputField label="Partner Age (optional)" value={data.partnerAge || ''} onChange={update('partnerAge')} type="number" placeholder="e.g. 60" />
        <InputField label="State Pension Age" value={data.statePensionAge || 67} onChange={update('statePensionAge')} type="number" placeholder="67" />
        <p className="text-xs text-gray-400 -mt-2 mb-3">UK state pension age — update if client has different entitlement.</p>

        {/* Couple Toggle */}
        <div className="border-t border-gray-100 my-4"></div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Are you planning for:</div>
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setData(prev => ({ ...prev, isCouple: false }))}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              !data.isCouple ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Individual
          </button>
          <button
            onClick={() => setData(prev => ({ ...prev, isCouple: true }))}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              data.isCouple ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Couple
          </button>
        </div>

        {/* Partner Details Section */}
        {data.isCouple && (
          <>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Partner Details</div>
            <InputField label="Partner Name" value={data.partnerName || ''} onChange={update('partnerName')} placeholder="Partner name" />

            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-6">
              <div className="text-xs text-green-700 font-medium">
                ✓ Couple mode — income, pensions and ISAs will show fields for both you and {data.partnerName || 'your partner'}
              </div>
            </div>
          </>
        )}

        {/* ── Load from Tally ─────────────────────────────────────────── */}
        <div style={{ marginTop: '24px' }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            letterSpacing: '0.08em',
            color: '#6b7280',
            textTransform: 'uppercase' as const,
            marginBottom: '12px',
          }}>
            LOAD FROM TALLY
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              type="email"
              placeholder="client@example.com"
              value={tallyEmail}
              onChange={e => { setTallyEmail(e.target.value); setTallyStatus('idle'); }}
              onKeyDown={e => e.key === 'Enter' && handleLoadFromTally()}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <button
              onClick={handleLoadFromTally}
              disabled={tallyLoading || !tallyEmail.trim()}
              style={{
                padding: '10px 16px',
                backgroundColor: tallyLoading || !tallyEmail.trim() ? '#9ca3af' : '#1e3a5f',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: tallyLoading || !tallyEmail.trim() ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap' as const,
              }}
            >
              {tallyLoading ? 'Loading...' : 'Load data'}
            </button>
          </div>

          {/* Status messages */}
          {tallyStatus === 'success' && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#166534',
            }}>
              ✓ Data loaded successfully — {tallyFieldCount} fields populated.
            </div>
          )}

          {tallyStatus === 'partial' && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#92400e',
            }}>
              ⚠ Data loaded but some fields are missing — check the highlighted tools and complete manually.
            </div>
          )}

          {tallyStatus === 'notfound' && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#991b1b',
            }}>
              No intake form found for that email address. Check the email and try again, or enter data manually.
            </div>
          )}

          {tallyStatus === 'error' && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#991b1b',
            }}>
              Could not connect to the intake server. Check your internet connection and try again.
            </div>
          )}

          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
            Enter the email address the client used on their intake form.
          </div>
        </div>
      </div>

      {/* RIGHT: Output */}
      <div className="flex-1 p-8 overflow-y-auto bg-gray-50">
        {isReady ? (
          <div className="max-w-xl">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mb-6">
              <div className="bg-ma-navy px-6 py-5">
                <div className="text-xs text-blue-200 uppercase tracking-widest mb-1">Financial Freedom Model</div>
                <div className="text-white text-xl font-semibold">{data.clientName}</div>
                <div className="text-blue-200 text-sm mt-0.5">{data.businessName}</div>
              </div>
              <div className="px-6 py-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Session</div>
                  <div className="text-sm font-medium text-gray-700">Session 1 — Personal</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Date</div>
                  <div className="text-sm font-medium text-gray-700">{data.sessionDate}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Adviser</div>
                  <div className="text-sm font-medium text-gray-700">{data.adviserName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Planning for</div>
                  <div className="text-sm font-medium text-gray-700">
                    {data.isCouple ? `${data.clientName} & ${data.partnerName || 'partner'}` : 'Individual'}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</div>
              <div>
                <div className="text-sm font-medium text-green-800">Ready to begin</div>
                <div className="text-xs text-green-600 mt-0.5">Client details confirmed. Click Next to proceed to Income.</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👤</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-600 mb-2">Enter client details</h2>
              <p className="text-sm text-gray-400">Fill in the client name, business name and adviser name to get started.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSetup;
